/*
 *  -----------------------------------------------------------------------
 *    <copyright file="unassign-visit-confirmation-dialog.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogContent } from '@mui/material';
import React from 'react';
// eslint-disable-next-line max-len
import { UnassignVisitConfirmanionDialogProps } from '@components/admin/unassign-visit-confirmation-dialog/unassign-visit-confirmation-dialog.interface';
import { useTranslation } from 'hooks';
import { GET_STUDY_VISIT_SCHEDULES, SAVE_STUDY_VISIT_SCHEDULE_VISIT } from 'queries/study-visit-schedules';
import Button from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogTitle } from 'views/components/core/dialog';
import './styles.scss';
import NotificationService from '@utils/notification';
import {
    saveStudyVisitScheduleVisit,
    saveStudyVisitScheduleVisitVariables,
} from '@queries/generated/study-visit-schedules.generated';

const UnassignVisitConfirmanionDialog = ({
    studyId,
    currentScheduleId,
    currentVisit,
    close,
    assignedItem,
}: UnassignVisitConfirmanionDialogProps) => {
    const { t } = useTranslation();

    const [saveVisit] = useMutation<saveStudyVisitScheduleVisit, saveStudyVisitScheduleVisitVariables>(
        SAVE_STUDY_VISIT_SCHEDULE_VISIT,
    );

    const { isCrf } = assignedItem;

    const onClose = (action?: string) => {
        close(action);
    };

    const unassignSubmit = () => {
        const { eventIds } = currentVisit;
        if (eventIds) {
            const unassignIndex = eventIds.indexOf(assignedItem.id);
            eventIds.splice(unassignIndex, 1);
        }

        const submitData = {
            studyId,
            visitScheduleId: currentScheduleId,
            visit: {
                id: currentVisit.id,
                active: currentVisit.active,
                sequenceNumber: currentVisit.sequenceNumber,
                type: currentVisit.type,
                visitId: currentVisit.visitId,
                visitName: currentVisit.visitName,
                leadVisitId: currentVisit.leadVisitId,
                leadPeriod: currentVisit.leadPeriod,
                leadUnit: currentVisit.leadUnit,
                minWinPeriod: currentVisit.minWinPeriod,
                maxWinPeriod: currentVisit.maxWinPeriod,
                minWinUnit: currentVisit.minWinUnit,
                minOccurrences: currentVisit.minOccurrences,
                forecastEndDateField: currentVisit.forecastEndDateField,
                isEndDateEarliest: currentVisit.isEndDateEarliest,
                eventIds,
            },
        };

        saveVisit({
            variables: { ...submitData },
            refetchQueries: [
                {
                    query: GET_STUDY_VISIT_SCHEDULES,
                    variables: {
                        studyId,
                    },
                },
            ],
        })
            .then(() => {
                NotificationService.showNotification(
                    t(isCrf ? 'admin:visitUnassignedMessage' : 'admin:visitUnassignedFromAssessMessage', {
                        itemId: assignedItem?.recordId,
                        visitId: currentVisit?.recordId || currentVisit?.id,
                    }),
                );

                onClose();
            })
            .catch(() => {
                onClose('error');
            });
    };

    return (
        <CustomDialog
            onClose={() => onClose()}
            fullWidth
            maxWidth="md"
            scroll="paper"
            open
            className="custom-width-popup"
        >
            <DialogTitle id="unassign-visit-crf-modal-title">
                {t('admin:unassignVisit')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => onClose()}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="unassign-visit-dialog-content">
                <div className="unassign-visit-text">
                    {t(isCrf ? 'admin:unassignCrfVisitQuestion' : 'admin:unassignAssessmentVisitQuestion')}
                </div>
                <div className="content-text">
                    <span className="title">{t(isCrf ? 'admin:crfName' : 'admin:assessmentName')}:</span>
                    {assignedItem.name}
                </div>
                <div className="content-text">
                    <span className="title">{t('subject:visitName')}:</span>
                    {currentVisit.visitName}
                </div>
                <DialogActions className="add-treatment-dialog-content-actions flex-end">
                    <Button id="unassign-visit-crf-btn" text={t('common:unassign')} onClick={unassignSubmit} />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default UnassignVisitConfirmanionDialog;
